var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import PlaybackManager from "./PlaybackManager";
import * as uuid from 'uuid';
var uuidv4 = uuid.v4;
/**
 * This enum is used to identify different types of playable items
 */
export var PlaybackListItemType;
(function (PlaybackListItemType) {
    PlaybackListItemType["Playlist"] = "playlist";
    PlaybackListItemType["Track"] = "track";
    PlaybackListItemType["CuratedSet"] = "curated_set";
})(PlaybackListItemType || (PlaybackListItemType = {}));
/**
 * This class manages a queue of playable items and their playback
 */
var PlaybackList = /** @class */ (function () {
    function PlaybackList() {
        this._tracks = [];
        this._shuffledTracks = [];
        this._playbackMode = 'serial';
        this._activeListListeners = {};
    }
    // Playlist Index Getters
    /**
     * Returns the index of the given track
     * @param track
     * @param raw This flag determines if the input list of tracks should be used (false) or the playback mode (shuffle/serial) should be taken into account (true)
     */
    PlaybackList.prototype.getTrackIndex = function (track, raw) {
        if (raw === void 0) { raw = false; }
        return (raw ? this._tracks : this.getTracks()).findIndex(function (item) {
            return item.sound.id == track.sound.id && item.playlist.isEqual(track.playlist);
        });
    };
    /**
     * Returns the index of the given track in the shuffled list
     * @param track
     */
    PlaybackList.prototype.getShuffledTrackIndex = function (track) {
        return this._shuffledTracks.findIndex(function (item) {
            return item.sound.id == track.sound.id && item.playlist.isEqual(track.playlist);
        });
    };
    // Track Getters
    /**
     * Returns the upcoming list of tracks after the given track
     * @param song
     */
    PlaybackList.prototype.getTracksAfter = function (song) {
        var idx = song ? this.getTrackIndex(song) : 0;
        return this.getTracks().slice(idx + 1);
    };
    /**
     * Returns the previous list of tracks after the given track
     * @param song
     */
    PlaybackList.prototype.getTracksBefore = function (song) {
        var idx = song ? this.getTrackIndex(song) : 0;
        return this.getTracks().slice(0, idx);
    };
    /**
     * Returns the track after the current track
     * @param song
     */
    PlaybackList.prototype.getTrackAfter = function (song) {
        var idx = song ? this.getTrackIndex(song) : 0;
        if (idx + 1 < this.getTracks().length) {
            return this.getTracks()[idx + 1];
        }
        return undefined;
    };
    /**
     * Returns the track before the current track
     * @param song
     */
    PlaybackList.prototype.getTrackBefore = function (song) {
        var idx = song ? this.getTrackIndex(song) : 0;
        if (idx - 1 >= 0) {
            return this.getTracks()[idx - 1];
        }
        return undefined;
    };
    // Util Getters
    /**
     * Returns all tracks that have been enqueued into the playback list
     */
    PlaybackList.prototype.getTracks = function () {
        return this._playbackMode === 'serial' ? this._tracks : this._shuffledTracks;
    };
    /**
     * Returns the current playback mode
     */
    PlaybackList.prototype.getPlaybackMode = function () {
        return this._playbackMode;
    };
    /**
     * Sets the current playback mode
     */
    PlaybackList.prototype.setPlaybackMode = function (mode) {
        this._shuffledTracks = this.shuffle(this._tracks);
        this._playbackMode = mode;
    };
    // Playlist Management
    /**
     * Appends a list to play after the current list
     */
    PlaybackList.prototype.appendNext = function (list) {
        var track = PlaybackManager.getInstance().getTrack();
        if (track) {
            var index = this.getTrackIndex(track);
            this.insertList(list, index + 1);
        }
        else {
            this.insertList(list, 0);
            PlaybackManager.getInstance().startTrack(this.getTracksAfter(undefined)[0]);
        }
    };
    PlaybackList.prototype.clear = function () {
        this._tracks = [];
        this._shuffledTracks = [];
        this.callActiveListChangedListeners(this._tracks);
        PlaybackManager.getInstance().stop();
    };
    /**
     * Replaces all currently enqueued lists with the given list
     * @param list
     */
    PlaybackList.prototype.replaceLists = function (list) {
        this._tracks = list.tracks();
        this._shuffledTracks = this.shuffle(this._tracks);
        this.callActiveListChangedListeners(this._tracks);
    };
    /**
     * Appends a list for playback
     * @param list
     */
    PlaybackList.prototype.appendList = function (list) {
        if (this._tracks.find(function (t) { return t.playlist.context == list.context; })) {
            return;
        }
        this._tracks = this._tracks.concat(list.tracks());
        this._shuffledTracks = this._shuffledTracks.concat(this.shuffle(list.tracks()));
        this.callActiveListChangedListeners(this._tracks);
    };
    /**
     * Inserts a list for playback at the given index
     * @param list
     */
    PlaybackList.prototype.insertList = function (list, atIndex) {
        var _a, _b;
        (_a = this._tracks).splice.apply(_a, __spreadArrays([atIndex, 0], list.tracks()));
        (_b = this._shuffledTracks).splice.apply(_b, __spreadArrays([atIndex, 0], this.shuffle(list.tracks())));
        this.callActiveListChangedListeners(this._tracks);
    };
    /**
     * Removes a track from the playback queue
     * @param list
     */
    PlaybackList.prototype.removeTrack = function (track) {
        var idx = this.getTrackIndex(track);
        this._tracks.splice(idx, 1);
        var idx2 = this.getShuffledTrackIndex(track);
        this._shuffledTracks.splice(idx2, 1);
        this.callActiveListChangedListeners(this._tracks);
    };
    // Change Listeners
    /**
     * Adds a listener for any changes in the active ilst
     * @param list
     */
    PlaybackList.prototype.addActiveListChangedListener = function (listener) {
        var id = uuidv4();
        this._activeListListeners[id] = listener;
        return id;
    };
    /**
     * Removes a listener for any changes in the active ilst
     * @param list
     */
    PlaybackList.prototype.removeActiveListChangedListener = function (id) {
        delete this._activeListListeners[id];
    };
    PlaybackList.prototype.callActiveListChangedListeners = function (tracks) {
        var _this = this;
        Object.keys(this._activeListListeners).map(function (key) { return _this._activeListListeners[key]; }).forEach(function (cb) {
            cb(tracks);
        });
    };
    PlaybackList.prototype.shuffle = function (inputdata) {
        var _a;
        var input = (_a = []).concat.apply(_a, inputdata);
        var track = PlaybackManager.getInstance().getTrack();
        var trackIndex = track && this.getTrackIndex(track);
        var playingTrack = trackIndex !== undefined && trackIndex >= 0 && input.splice(trackIndex, 1);
        var output = input.slice();
        for (var i = output.length - 1; i >= 0; i--) {
            var randomIndex = Math.floor(Math.random() * (i + 1));
            var itemAtIndex = output[randomIndex];
            output[randomIndex] = output[i];
            output[i] = itemAtIndex;
        }
        trackIndex !== undefined && trackIndex >= 0 && playingTrack && playingTrack.length == 1 && output.splice.apply(output, __spreadArrays([trackIndex, 0], playingTrack));
        return output;
    };
    return PlaybackList;
}());
export default PlaybackList;
//# sourceMappingURL=PlaybackList.js.map